import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import ShareCard from './ShareCard';
import { FORMAT_SPECS, SHARE_FORMATS } from './share/ShareCardFormats';
import { PlatformMark, Glyph, hasOfficialIcon } from './share/PlatformIcons';
import {
  generateShareText,
  copyToClipboard,
  copyTextDeferred,
  generateShareImage,
  downloadImage,
  dataURLtoBlob,
  prepareShareImage,
  resolveShareName,
  ANON_SHARE_NAME,
} from '../../utils/shareUtils';
import {
  getVisiblePlatforms,
  isMobileDevice,
  canShareFiles,
  buildShareUrl,
} from '../../utils/sharePlatforms';
import { formatLevelDisplay } from '../../utils/levelUtils';
import {
  uploadShareImage,
  getUserPreferences,
  updateUserPreferences,
  createPublicShare,
  trackShare,
} from '../../firebase/services';
import { AuthContext } from '../../context/AuthContext';

/**
 * Share sheet (Release 14).
 * Tap a platform -> land in its composer. See PRD_RELEASE_14.md.
 */

// ─── Styles ───────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.md};
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  @media (max-width: 600px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Sheet = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.large};
  max-width: 640px;
  width: 100%;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.large};
  animation: slideUp 0.25s ease-out;

  @keyframes slideUp {
    from { transform: translateY(24px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @media (max-width: 600px) {
    border-radius: 20px 20px 0 0;
    max-height: 94vh;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.softGray};
  position: sticky;
  top: 0;
  background-color: ${theme.colors.white};
  z-index: 10;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.xlarge};
  color: ${theme.colors.deepNavy};
  margin: 0;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.slateGray};
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  &:hover { background-color: ${theme.colors.softGray}; }
`;

const Content = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.lg};
  @media (max-width: 600px) { padding: ${theme.spacing.md}; }
`;

const SectionLabel = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.slateGray};
  margin: 0 0 ${theme.spacing.sm};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Banner = styled.div`
  background: rgba(255, 209, 0, 0.14);
  border: 1px solid rgba(255, 209, 0, 0.6);
  border-radius: ${theme.borderRadius.medium};
  padding: 12px 14px;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: ${theme.colors.deepNavy};
  line-height: 1.45;
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const PillButton = styled.button`
  border-radius: 999px;
  padding: 8px 16px;
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => (p.$primary ? 'transparent' : theme.colors.softGray)};
  background: ${p => (p.$primary ? theme.colors.brandYellow : theme.colors.white)};
  color: ${theme.colors.deepNavy};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Tabs = styled.div`
  display: inline-flex;
  background: ${theme.colors.background};
  border-radius: 999px;
  padding: 3px;
  margin-bottom: ${theme.spacing.sm};
`;

const Tab = styled.button`
  border: none;
  border-radius: 999px;
  padding: 6px 14px;
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => (p.$active ? theme.colors.white : 'transparent')};
  color: ${p => (p.$active ? theme.colors.deepNavy : theme.colors.slateGray)};
  box-shadow: ${p => (p.$active ? theme.shadows.small : 'none')};
`;

const PreviewBox = styled.div`
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing.md};
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 280px;
    object-fit: contain;
    border-radius: 6px;
    box-shadow: ${theme.shadows.medium};
  }
`;

const CaptionPreview = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  font-size: 13px;
  white-space: pre-wrap;
  max-height: 96px;
  overflow-y: auto;
`;

const Muted = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  color: ${theme.colors.slateGray};
`;

const CaptionBox = styled.textarea`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  padding: 12px;
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: ${theme.colors.deepNavy};
  line-height: 1.5;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 96px;
  outline: none;
  &:focus { border-color: ${theme.colors.brandYellow}; background-color: ${theme.colors.white}; }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #999;
  font-style: italic;
  margin: 6px 0 ${theme.spacing.md};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 14px 6px;
  margin-bottom: ${theme.spacing.lg};
`;

const Tile = styled.button`
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: ${theme.colors.deepNavy};
  position: relative;

  &:disabled { cursor: progress; opacity: 0.55; }
  &:focus-visible { outline: 2px solid ${theme.colors.brandYellow}; outline-offset: 2px; border-radius: 8px; }
`;

const TileCircle = styled.span`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => (p.$official ? theme.colors.white : p.$bg)};
  border: ${p => (p.$official ? `1px solid ${theme.colors.softGray}` : 'none')};
  box-sizing: border-box;
  color: ${p => (p.$dark ? theme.colors.deepNavy : theme.colors.white)};
  transition: transform 0.15s ease;
  ${Tile}:hover:not(:disabled) & { transform: translateY(-2px); }
  ${Tile}:active:not(:disabled) & { transform: scale(0.95); }
`;

const Spinner = styled.span`
  width: 22px;
  height: 22px;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid ${theme.colors.softGray};
  background: ${theme.colors.white};
  color: ${theme.colors.deepNavy};
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${theme.colors.background}; }
  &:disabled { opacity: 0.5; cursor: progress; }
`;

const HiddenCards = styled.div`
  position: fixed;
  left: -10000px;
  top: 0;
  pointer-events: none;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  max-width: min(92vw, 420px);
  text-align: center;
  background: ${theme.colors.deepNavy};
  color: ${theme.colors.white};
  padding: 12px 18px;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  font-weight: 500;
  z-index: 2000;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────

const nextFrame = () => new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

const openInNewTab = (url) => {
  const w = window.open(url, '_blank');
  if (w) w.opener = null;
  return !!w;
};

// ─── Component ────────────────────────────────────────────────────────────

const ShareModal = ({ activity, onClose }) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);

  const srcUrl = activity.imageUrl || activity.thumbnailUrl;
  const [shareText, setShareText] = useState(() => generateShareText(activity));
  const [prefs, setPrefs] = useState(null);
  const [photos, setPhotos] = useState({});
  const [photosReady, setPhotosReady] = useState(!srcUrl);
  const [cards, setCards] = useState({});
  const [previewFormat, setPreviewFormat] = useState('feed');
  const [busy, setBusy] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(null);
  const [toast, setToast] = useState(null);

  const storyRef = useRef(null);
  const feedRef = useRef(null);
  const linkRef = useRef(null);
  const cardRefs = useMemo(() => ({ story: storyRef, feed: feedRef, link: linkRef }), []);
  const cardsRef = useRef({});
  const publishPromise = useRef(null);
  const published = useRef(null);
  const toastTimer = useRef(null);

  const mobile = useMemo(() => isMobileDevice(), []);
  const fileShare = useMemo(() => canShareFiles(), []);
  const platforms = useMemo(() => getVisiblePlatforms({ mobile, fileShare }), [mobile, fileShare]);

  // Name mode from Privacy Settings; under-18s are always anonymous.
  const userName = useMemo(
    () => (prefs ? resolveShareName(prefs, currentUser?.displayName) : null),
    [prefs, currentUser]
  );

  const showToast = useCallback((message, ms = 3500) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Load share preferences
  useEffect(() => {
    if (!currentUser) return;
    getUserPreferences(currentUser.uid)
      .then(setPrefs)
      .catch(() => setPrefs({ shareAnonymous: false, isAdultConfirmed: null }));
  }, [currentUser]);

  // Pre-render the photo into each format's slot (CORS-safe data URLs, 2x)
  useEffect(() => {
    if (!srcUrl) { setPhotosReady(true); return undefined; }
    let cancelled = false;
    setPhotosReady(false);
    Promise.all(SHARE_FORMATS.map(f => {
      const { w, h } = FORMAT_SPECS[f].photo;
      return prepareShareImage(srcUrl, { maxWidth: w, maxHeight: h, pixelRatio: 2 }).catch(() => null);
    })).then(results => {
      if (cancelled) return;
      const next = {};
      SHARE_FORMATS.forEach((f, i) => { if (results[i]) next[f] = results[i]; });
      setPhotos(next);
      setPhotosReady(true);
    });
    return () => { cancelled = true; };
  }, [srcUrl]);

  const renderCard = useCallback(async (format) => {
    const el = cardRefs[format].current;
    if (!el) throw new Error('card not mounted');
    const dataUrl = await generateShareImage(el, { scale: 2, backgroundColor: FORMAT_SPECS[format].bg });
    cardsRef.current = { ...cardsRef.current, [format]: dataUrl };
    setCards(cardsRef.current);
    return dataUrl;
  }, [cardRefs]);

  // Generate all card images locally (nothing is uploaded here).
  // Re-runs if the displayed name changes; any published link is discarded.
  useEffect(() => {
    if (!photosReady || userName === null) return undefined;
    let cancelled = false;
    cardsRef.current = {};
    setCards({});
    publishPromise.current = null;
    published.current = null;
    (async () => {
      await nextFrame();
      for (const f of ['feed', 'story', 'link']) {
        if (cancelled) return;
        try { await renderCard(f); } catch (e) { console.error(`Share card (${f}) failed`, e); }
      }
    })();
    return () => { cancelled = true; };
  }, [photosReady, userName, renderCard]);

  const ensureCard = useCallback(
    (format) => cardsRef.current[format] ? Promise.resolve(cardsRef.current[format]) : renderCard(format),
    [renderCard]
  );

  const track = useCallback((shareType, format) => {
    if (!currentUser) return;
    trackShare({ userId: currentUser.uid, activityId: activity.id || null, shareType, format: format || null });
  }, [currentUser, activity.id]);

  /** Upload the link card and create the public share page (once per session). */
  const publish = useCallback(() => {
    if (publishPromise.current) return publishPromise.current;
    publishPromise.current = (async () => {
      const dataUrl = await ensureCard('link');
      const imageUrl = await uploadShareImage(
        dataURLtoBlob(dataUrl), currentUser.uid, activity.id || Date.now().toString()
      );
      const id = await createPublicShare({
        userId: currentUser.uid,
        activityId: activity.id,
        title: activity.title,
        causeTitle: activity.cause?.title,
        levelTitle: activity.cause?.level ? formatLevelDisplay(activity.cause.level) : '',
        reflection: activity.reflection,
        displayName: userName || ANON_SHARE_NAME,
        imageUrl,
        photoUrl: activity.imageUrl || '',
      });
      published.current = { id, url: buildShareUrl(id) };
      return published.current;
    })().catch(err => {
      publishPromise.current = null;
      throw err;
    });
    return publishPromise.current;
  }, [ensureCard, currentUser, activity, userName]);

  const composeArgs = (url) => ({
    text: shareText,
    url,
    title: [activity.title, activity.cause?.title].filter(Boolean).join(' — '),
    subject: t('share.emailSubject'),
  });

  const platformLabel = (p) => t(`share.platforms.${p.id}`, { defaultValue: p.label });

  // ── Native share sheet (Instagram, TikTok, More) ──
  const handleNative = (p) => {
    const dataUrl = cardsRef.current[p.format];
    if (!dataUrl) { showToast(t('share.preparing')); return; }
    const label = platformLabel(p);
    const isMore = p.id === 'more';

    if (fileShare && (mobile || isMore)) {
      const file = new File([dataURLtoBlob(dataUrl)], `lightway-${p.format}.png`, { type: 'image/png' });
      if (!isMore) {
        copyToClipboard(shareText);
        showToast(t('share.nativeHint', { platform: label }), 6000);
      }
      const payload = isMore ? { files: [file], text: shareText, title: activity.title } : { files: [file] };
      navigator.share(payload)
        .then(() => track(p.id, p.format))
        .catch(err => { if (err?.name !== 'AbortError') showToast(t('share.imageFailed')); });
      return;
    }

    // Desktop: save image, copy caption, open the site
    downloadImage(dataUrl, `lightway-${p.format}-${activity.id || 'activity'}`);
    copyToClipboard(shareText);
    if (p.desktopUrl) openInNewTab(p.desktopUrl);
    showToast(t('share.desktopHint', { platform: label }), 6000);
    track(p.id, p.format);
  };

  // ── Link platforms (need the public share page) ──
  const handleLinkPlatform = async (p) => {
    const label = platformLabel(p);

    if (p.kind === 'copyOpen') {
      // Open the destination from the user's own click (via Continue in the banner),
      // not from this handler — the toast was being drawn on the Lightway tab that
      // immediately lost focus to the new tab, so testers never saw the instructions.
      const copied = copyTextDeferred(publish().then(({ url }) => `${shareText}\n\n${url}`));
      setBusy(p.id);
      try {
        await publish();
        setPendingOpen({
          label,
          url: p.desktopUrl,
          caption: true,
          copied: await copied,
          hintKey: p.hintKey,
          platformId: p.id,
          format: p.format,
        });
      } catch (e) {
        console.error('Share publish failed', e);
        showToast(t('share.linkFailed'));
      } finally {
        setBusy(null);
      }
      return;
    }

    // Platforms that drop prefilled text (Facebook): copy the caption during the tap,
    // then show a confirm step whose Continue button opens the composer.
    if (p.confirmCaption) {
      const copied = copyToClipboard(shareText);
      setBusy(p.id);
      try {
        const { url } = published.current || await publish();
        setPendingOpen({ label, url: p.buildUrl(composeArgs(url)), caption: true, copied: await copied, platformId: p.id, format: p.format });
      } catch (e) {
        console.error('Share publish failed', e);
        showToast(t('share.linkFailed'));
      } finally {
        setBusy(null);
      }
      return;
    }

    // Already published: open synchronously so apps can launch directly
    if (published.current) {
      const target = p.buildUrl(composeArgs(published.current.url));
      if (p.kind === 'scheme') window.location.href = target;
      else if (!openInNewTab(target)) setPendingOpen({ label, url: target });
      track(p.id, p.format);
      return;
    }

    // First publish: reserve the tab during the tap, fill it when ready
    const win = p.kind === 'intent' ? window.open('', '_blank') : null;
    setBusy(p.id);
    try {
      const { url } = await publish();
      const target = p.buildUrl(composeArgs(url));
      if (p.kind === 'scheme') {
        window.location.href = target;
        setPendingOpen({ label, url: target, scheme: true });
      } else if (win && !win.closed) {
        win.opener = null;
        win.location.href = target;
      } else if (!openInNewTab(target)) {
        setPendingOpen({ label, url: target });
      }
      track(p.id, p.format);
    } catch (e) {
      console.error('Share publish failed', e);
      if (win) win.close();
      showToast(t('share.linkFailed'));
    } finally {
      setBusy(null);
    }
  };

  const handlePlatform = (p) => {
    setPendingOpen(null);
    if (p.kind === 'native') handleNative(p);
    else handleLinkPlatform(p);
  };

  // ── Secondary actions ──
  const handleCopyLink = async () => {
    setBusy('copy-link');
    const ok = await copyTextDeferred(publish().then(r => r.url));
    setBusy(null);
    showToast(ok ? t('share.linkCopied') : t('share.linkFailed'));
    if (ok) track('copy-link', 'link');
  };

  const handleCopyCaption = async () => {
    const ok = await copyToClipboard(shareText);
    showToast(ok ? t('share.textCopied') : t('share.copyFailed'));
    if (ok) track('copy-text');
  };

  const handleSaveImage = async () => {
    try {
      const dataUrl = await ensureCard(previewFormat);
      downloadImage(dataUrl, `lightway-${previewFormat}-${activity.id || 'activity'}`);
      showToast(t('share.imageSaved'));
      track('download-image', previewFormat);
    } catch {
      showToast(t('share.imageFailed'));
    }
  };

  const answerAge = (isAdult) => {
    setPrefs(prev => ({ ...prev, isAdultConfirmed: isAdult }));
    if (currentUser) {
      updateUserPreferences(currentUser.uid, { isAdultConfirmed: isAdult })
        .catch(err => console.error('Failed to save age confirmation', err));
    }
  };

  const cardsReady = (format) => Boolean(cards[format]);
  const hiddenActivity = activity;

  return (
    <>
      <Overlay onClick={onClose}>
        <Sheet role="dialog" aria-modal="true" aria-labelledby="share-title" onClick={e => e.stopPropagation()}>
          <Header>
            <Title id="share-title">{t('share.title')}</Title>
            <CloseButton onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}>×</CloseButton>
          </Header>

          <Content>
            {prefs && prefs.isAdultConfirmed == null && (
              <Banner>
                <strong>{t('share.ageQuestion')}</strong> {t('share.ageExplainer')}
                <BannerActions>
                  <PillButton $primary onClick={() => answerAge(true)}>{t('share.ageYes')}</PillButton>
                  <PillButton onClick={() => answerAge(false)}>{t('share.ageNo')}</PillButton>
                </BannerActions>
              </Banner>
            )}

            {pendingOpen && pendingOpen.caption && (
              <Banner role="status">
                {pendingOpen.hintKey ? (
                  <>
                    {!pendingOpen.copied && (
                      <><strong>{t('share.captionStepNotCopied')}</strong>{' '}</>
                    )}
                    {t(pendingOpen.hintKey)}
                  </>
                ) : (
                  <>
                    <strong>{pendingOpen.copied ? t('share.captionStepCopied') : t('share.captionStepNotCopied')}</strong>{' '}
                    {t('share.captionStepBody', { platform: pendingOpen.label })}
                  </>
                )}
                <CaptionPreview>{shareText}</CaptionPreview>
                <BannerActions>
                  <PillButton
                    as="a"
                    $primary
                    href={pendingOpen.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { track(pendingOpen.platformId, pendingOpen.format); setPendingOpen(null); }}
                  >
                    {t('share.continueTo', { platform: pendingOpen.label })}
                  </PillButton>
                  <PillButton
                    onClick={async () => {
                      const ok = await copyToClipboard(shareText);
                      setPendingOpen(prev => prev && { ...prev, copied: ok || prev.copied });
                      showToast(ok ? t('share.textCopied') : t('share.copyFailed'));
                    }}
                  >
                    <Glyph name="copy" size={16} /> {t('share.copyCaption')}
                  </PillButton>
                </BannerActions>
              </Banner>
            )}

            {pendingOpen && !pendingOpen.caption && (
              <Banner>
                {t('share.readyToOpen')}
                <BannerActions>
                  <PillButton
                    as="a"
                    $primary
                    href={pendingOpen.url}
                    target={pendingOpen.scheme ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    onClick={() => setPendingOpen(null)}
                  >
                    {t('share.openPlatform', { platform: pendingOpen.label })}
                  </PillButton>
                </BannerActions>
              </Banner>
            )}

            <SectionLabel>{t('share.shareTo')}</SectionLabel>
            <Grid>
              {platforms.map(p => {
                const needsCard = p.kind === 'native';
                const disabled = busy === p.id || (needsCard && !cardsReady(p.format));
                return (
                  <Tile key={p.id} onClick={() => handlePlatform(p)} disabled={disabled} aria-label={platformLabel(p)}>
                    <TileCircle $bg={p.color} $dark={p.dark} $official={hasOfficialIcon(p) && busy !== p.id}>
                      {busy === p.id ? <Spinner /> : <PlatformMark platform={p} />}
                    </TileCircle>
                    {platformLabel(p)}
                  </Tile>
                );
              })}
            </Grid>

            <SectionLabel>{t('share.captionLabel')}</SectionLabel>
            <CaptionBox
              value={shareText}
              onChange={e => setShareText(e.target.value)}
              aria-label={t('share.captionLabel')}
            />
            <Hint>{t('share.captionHint')}</Hint>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <Tabs role="tablist">
                {[['feed', 'formatFeed'], ['story', 'formatStory'], ['link', 'formatLink']].map(([f, key]) => (
                  <Tab key={f} role="tab" aria-selected={previewFormat === f} $active={previewFormat === f} onClick={() => setPreviewFormat(f)}>
                    {t(`share.${key}`)}
                  </Tab>
                ))}
              </Tabs>
              {userName && <Muted>{t('share.shownAs', { name: userName })}</Muted>}
            </div>
            <PreviewBox>
              {cards[previewFormat]
                ? <img src={cards[previewFormat]} alt={t('share.previewAlt')} />
                : <Muted>{t('share.preparing')}</Muted>}
            </PreviewBox>

            <ActionRow>
              <ActionButton onClick={handleCopyLink} disabled={busy === 'copy-link'}>
                <Glyph name="link" size={16} /> {t('share.copyLink')}
              </ActionButton>
              <ActionButton onClick={handleCopyCaption}>
                <Glyph name="copy" size={16} /> {t('share.copyCaption')}
              </ActionButton>
              <ActionButton onClick={handleSaveImage} disabled={!cardsReady(previewFormat)}>
                <Glyph name="download" size={16} /> {t('share.saveImage')}
              </ActionButton>
            </ActionRow>
          </Content>
        </Sheet>
      </Overlay>

      {/* Off-screen cards rendered at export size for html2canvas */}
      {photosReady && userName !== null && (
        <HiddenCards aria-hidden="true">
          {SHARE_FORMATS.map(f => (
            <div key={f} style={{ marginBottom: 20 }}>
              <ShareCard
                ref={cardRefs[f]}
                format={f}
                activity={hiddenActivity}
                userName={userName}
                photo={photos[f]}
              />
            </div>
          ))}
        </HiddenCards>
      )}

      {toast && <Toast role="status">{toast}</Toast>}
    </>
  );
};

export default ShareModal;
